import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Encoding, EncodingResult } from '@mode-vegas/vegas';
import { EncodingHelpers, FieldFormatHelpers } from '@mode/charts/util-vegas';
import {
  ChartTypes,
  ConditionalFormattingTypes,
  DataProfilingTypes,
  FlamingoTypes,
  ROW_HEIGHT_ATTR,
  SortSettingsTypes,
} from '@mode/shared/contract-common';
import {
  getDefaultFieldFormatByColumn,
  isBuilderMode,
  isPhantomMode,
  isReportMode,
  updateColumnWidth,
  updatePageSizeInFormat,
} from '@mode/shared/util-js';
import { CompiledField, Switch } from '@mode/shared/util-switch';

interface FilterFieldEncoding {
  model: FlamingoTypes.ModeModel;
  encoding: Encoding;
  queryToken: string;
  name: string;
}

@Component({
  selector: 'mode-table-renderer',
  templateUrl: './table-renderer.component.html',
  styleUrls: ['./table-renderer.component.scss'],
})
export class TableRendererComponent implements OnChanges, OnDestroy, OnInit {
  @Input() token = '';
  @Input() columns!: FlamingoTypes.RecordsResult['columns'];
  @Input() content!: FlamingoTypes.RecordsResult['content'];
  @Input() paging!: FlamingoTypes.PagingResult;
  @Input() isDesigner = false;
  @Input() isViewPage = false;
  @Input() showFilterMenuOption = true;
  @Input() showRemoveMenuOption = true;
  @Input() format!: ChartTypes.ChartFormat;
  @Input() fieldFormats!: ChartTypes.FieldFormatMap;
  @Input() activeTableColumn = '';
  @Input() customConfig!: ChartTypes.FlatTableCustomConfig;
  @Input() encoding!: Encoding;
  @Input() model!: FlamingoTypes.ModeModel;
  @Input() queryToken = '';
  @Input() autoHeight!: boolean;
  @Input() dataProfiling!: DataProfilingTypes.DataProfilingMap;
  @Input() conditionalFormatting!: ConditionalFormattingTypes.ConditionalFormattingMap;
  @Input() showColumnMenu = true;
  @Input() colorPaletteToken!: string;

  @Output() setOffset: EventEmitter<number> = new EventEmitter<number>();
  @Output() setPageSize: EventEmitter<number> = new EventEmitter<number>();
  @Output() updateFormat: EventEmitter<ChartTypes.ChartFormat> = new EventEmitter<ChartTypes.ChartFormat>();
  @Output() updateEncoding: EventEmitter<EncodingResult> = new EventEmitter<EncodingResult>();
  @Output() updateFilterFieldEncoding: EventEmitter<FilterFieldEncoding> = new EventEmitter<FilterFieldEncoding>();
  @Output() openFieldFormatEditModal: EventEmitter<{
    columnId: string;
    fieldFormats: ChartTypes.FieldFormatMap;
    encoding: Encoding;
  }> = new EventEmitter<{ columnId: string; fieldFormats: ChartTypes.FieldFormatMap; encoding: Encoding }>();
  @Output() setColumnSelection: EventEmitter<string> = new EventEmitter<string>();
  @Output() setTableColumnSearch: EventEmitter<{
    token: string;
    columnSearchModel: ChartTypes.TableColumnSearch['model'];
  }> = new EventEmitter<{ token: string; columnSearchModel: ChartTypes.TableColumnSearch['model'] }>();
  @Output() renderStart = new EventEmitter();
  @Output() renderEnd = new EventEmitter<any>();
  @Output() setTableHeight: EventEmitter<void> = new EventEmitter<void>();
  @Output() getDataProfiling: EventEmitter<DataProfilingTypes.DataProfilingChartDetails> =
    new EventEmitter<DataProfilingTypes.DataProfilingChartDetails>();
  @Output() handleSortSettings = new EventEmitter<SortSettingsTypes.SortSettingsEvent>();
  @Output() openConditionalFormattingModal: EventEmitter<{
    columnId: string;
    encoding: Encoding;
  }> = new EventEmitter<{
    columnId: string;
    encoding: Encoding;
  }>();

  isReport: boolean;
  isPhantom: boolean;
  isBuilder: boolean;
  domLayout = 'normal';

  resolvedFieldFormatMap: ChartTypes.FieldFormatMap = {};

  constructor(
    private element: ElementRef,
    private window: Window
  ) {
    this.isReport = isReportMode(this.window.document.body);
    this.isBuilder = isBuilderMode(this.window.document.body);
    this.isPhantom = isPhantomMode(this.window);
  }

  ngOnInit() {
    if (this.element.nativeElement.closest('.row')?.getAttribute(ROW_HEIGHT_ATTR) === 'auto') {
      this.domLayout = 'autoHeight';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // The responsibility is being "translated" to `domLayout` because only the builder
    // context will ever pass down a value or changes to `autoHeight`, and this feels more
    // clear describing what's going on.
    if (changes.autoHeight && changes.autoHeight.currentValue !== undefined) {
      this.domLayout = changes.autoHeight.currentValue ? 'autoHeight' : 'normal';
    }

    this.resolveFieldFormats();
  }

  ngOnDestroy() {
    // When the flat table is destroyed, it dispatches a renderStart. We need to
    // make sure that we send a renderEnd when the table renderer is destroyed.
    this.renderEnd.emit();
  }

  resolveFieldFormats() {
    const view = this.model ? Switch.Views.find(this.model, this.queryToken) : undefined;
    if (!view || !this.encoding) {
      // Unable to get the default formats. Just use the fieldFormats.
      this.resolvedFieldFormatMap = { ...(this.fieldFormats ?? {}) };
      return;
    }

    this.resolvedFieldFormatMap = this.columns?.reduce((fieldFormats, column) => {
      const encField = EncodingHelpers.getEncFieldById(this.encoding, column.alias);
      if (encField) {
        const compiled = CompiledField.fromEncField(view, encField);

        const fieldFormat = this.fieldFormats?.[encField.formula];

        const resolvedTextFormat =
          FieldFormatHelpers.resolveTextFormatForCompiledField(
            compiled,
            fieldFormat?.[ChartTypes.FieldFormatTypes.Pane]?.[ChartTypes.FieldPaneTypes.Default]
          ) ??
          getDefaultFieldFormatByColumn(column)[ChartTypes.FieldFormatTypes.Pane]?.[ChartTypes.FieldPaneTypes.Default];

        fieldFormats[encField.formula] = {
          ...fieldFormat,
          [ChartTypes.FieldFormatTypes.Pane]: { [ChartTypes.FieldPaneTypes.Default]: resolvedTextFormat },
        };
      }
      return fieldFormats;
    }, {} as ChartTypes.FieldFormatMap) ?? { ...(this.fieldFormats ?? {}) };
  }

  /**
   * Handle the encoding building here, so components that don't have an encoding can handle
   * this on their own (like flamingo results grid)
   */
  updateSort(args: { column: string; descending: boolean }): void {
    const { column, descending } = args;
    const encResult = EncodingHelpers.addTableSort(this.encoding, column, descending);
    this.updateEncoding.emit(encResult);
  }

  handleSortSettingsChange($event: { column: string; descending: boolean }) {
    this.handleSortSettings.emit({
      selectedColumn: $event.column,
      descending: $event.descending,
      encoding: this.encoding,
    });
  }

  /**
   * Handler for the offset change event.
   */
  handleOffsetChange(offset: number): void {
    this.setOffset.emit(offset);
  }

  handleSetTableHeight(): void {
    this.setTableHeight.emit();
  }

  /**
   * Handler for the table column search change event.
   */
  handleColumnWidthChange({ newWidth, formula }: { newWidth: number; formula: string }): void {
    const newFormat = updateColumnWidth(newWidth, formula, this.format);
    this.updateFormat.emit(newFormat);
  }

  /**
   * Handler for the table column search change event.
   */
  handleTableColumnSearchChange(columnSearchModel: ChartTypes.TableColumnSearch): void {
    this.setTableColumnSearch.emit({
      token: this.token,
      columnSearchModel: columnSearchModel,
    });
  }

  /**
   * Handler for the sort clear event.
   */
  handleSortClear(column: string): void {
    const encResult = EncodingHelpers.removeSort(this.encoding, column);
    this.updateEncoding.emit(encResult);
  }

  /**
   * Handler for the field removal event.
   */
  handleFieldRemoval(fieldId: string): void {
    const encResult = EncodingHelpers.removeFieldsFromEncoding(this.encoding, [fieldId]);
    this.updateEncoding.emit(encResult);
  }

  /**
   * Handler for the filter field change event.
   */
  handleFilterFieldChange(colName: string): void {
    this.updateFilterFieldEncoding.emit({
      model: this.model,
      encoding: this.encoding,
      queryToken: this.queryToken,
      name: colName,
    });
  }

  /**
   * Handler for the field format edit event.
   */
  handleFieldFormatEdit(formula: string): void {
    this.openFieldFormatEditModal.emit({
      columnId: formula,
      fieldFormats: this.resolvedFieldFormatMap,
      encoding: this.encoding,
    });
  }

  /**
   * Handler for the column selection change event.
   */
  handleColumnSelectionChange(column: string): void {
    this.setColumnSelection.emit(column);
  }

  /**
   * Handler for the conditional formatting event.
   */
  handleConditionalFormatting(formula: string): void {
    this.openConditionalFormattingModal.emit({
      columnId: formula,
      encoding: this.encoding,
    });
  }

  start(): void {
    this.renderStart.emit();
  }

  end(): void {
    this.renderEnd.emit();
  }

  handleGetDataProfiling($event: DataProfilingTypes.DataProfilingChartDetails): void {
    this.getDataProfiling.emit($event);
  }

  handlePageSizeChange(event: { pageSize: number; isUndoAction: boolean }) {
    const newFormat = updatePageSizeInFormat(this.format, event.pageSize);
    if (!event.isUndoAction) {
      this.updateFormat.emit(newFormat);
    }
    this.setPageSize.emit(event.pageSize);
  }
}
